import { DoubleRightOutlined, RedoOutlined } from '@ant-design/icons';
import {
  Table,
  Paging,
  usePaging,
  useUpdate,
  EllipsisSpan,
  Button,
  Input,
  CustomFilter,
  Select,
  MultiSelect,
} from '@maxtropy/components';
import { Form } from 'antd';
import { useRequest } from 'ahooks';
import { FC, Key, useEffect, useState } from 'react';
import { AlarmStatus, alarmStatusDisplay, RuleGroupPageProps, alarmStatusOptions } from './types';

import styles from './index.module.scss';
import { Link } from 'react-router-dom';
import { BatchRuleGroupPageRequest } from '../../../../api/ruleGroup';
import {
  apiV2RuleRulePagePost,
  apiV2RuleGroupDeviceAllRuleGroupPost,
  apiV2ComprehensiveLabelListPost,
} from '@maxtropy/device-customer-apis-v2';

export interface SelectRuleGroupModalChildrenProps {
  disabled: boolean;
  deviceId: Key;
  deviceTypeId?: number;
  iotProtocol?: number;
  value?: number[];
  physicalModelId?: number;
  onChange?: (val: number[]) => void;
  unBind?: () => void;
}

type SearchParams = Omit<BatchRuleGroupPageRequest, 'iotProtocol' | 'size' | 'page'>;

const SelectRuleGroupModalChildren: FC<SelectRuleGroupModalChildrenProps> = ({
  deviceId,
  disabled,
  deviceTypeId,
  iotProtocol,
  value,
  physicalModelId,
  onChange,
  unBind,
}) => {
  const [form] = Form.useForm();

  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [ruleGroups, setRuleGroups] = useState<RuleGroupPageProps[]>([]);
  const [nameOptions, setNameOptions] = useState<RuleGroupPageProps[]>([]);
  const [selectRowsKeys, setSelectRowsKeys] = useState<React.Key[]>([]);
  const [updateState, updateFn] = useUpdate();

  const [loading, setLoading] = useState<boolean>(false);
  const { data: labelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 2 }));

  useEffect(() => {
    setLoading(true);
    if (!deviceTypeId) return;
    apiV2RuleRulePagePost({
      ...searchParams,
      typeId: deviceTypeId,
      // physicalModelId,
      // iotProtocol: iotProtocol as 1 | 2 | 3 | undefined,
      page: pageOffset,
      size: pageSize,
    })
      .then(res => {
        setLoading(false);
        if (res) {
          setTotalCount(res.total ?? 0);
          res.list && setRuleGroups(res.list);
          setSelectRowsKeys((value as Key[]) ?? []);
        }
      })
      .finally(() => setLoading(false));
  }, [iotProtocol, pageOffset, pageSize, searchParams, setTotalCount, updateState]);

  useEffect(() => {
    //  规则组名称下拉  只传两个参数
    if (!deviceTypeId) return;
    apiV2RuleGroupDeviceAllRuleGroupPost({
      deviceTypeId,
      // physicalModelId,
      iotProtocol: iotProtocol as 1 | 2 | 3 | undefined,
    }).then(res => {
      res.list && setNameOptions(res.list);
    });
  }, [deviceTypeId, iotProtocol]);

  const columns = [
    {
      title: '规则名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '规则状态',
      dataIndex: 'status',
      ellipsis: { showTitle: true },
      render: (v: AlarmStatus) => <EllipsisSpan value={alarmStatusDisplay[v]} />,
    },
    {
      title: '规则编号',
      dataIndex: 'code',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属规则组',
      dataIndex: 'ruleGroupName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '报警标签',
      dataIndex: 'tags',
      ellipsis: { showTitle: true },
      render: (v: any) => {
        return <EllipsisSpan value={v ? v.join(',') : '--'} />;
      },
    },
  ];

  const onFinish = (value: SearchParams) => {
    setSearchParams(value);
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    form.setFieldsValue({
      codeOrNameLike: undefined,
      ruleGroupIdList: undefined,
    });
    setPageOffset(1);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (selectedRows: React.Key[]) => {
      setSelectRowsKeys(selectedRows);
      onChange?.(selectedRows.map(r => Number(r)));
    },
    getCheckboxProps: () => ({
      disabled: disabled, // Column configuration not to be checked
    }),
    selectedRowKeys: selectRowsKeys,
  };

  const filters = (
    <CustomFilter form={form} onFinish={val => onFinish(val as SearchParams)} colSpan={8} onReset={onReset}>
      <Form.Item name="codeOrNameLike" label="编号/名称">
        <Input placeholder="请输入编号或名称查询" />
      </Form.Item>

      <Form.Item label="规则组名称" name="ruleGroupIdList">
        <MultiSelect
          placeholder="请选择"
          showSearch
          allowClear
          options={nameOptions.map(i => ({ label: i.name, value: i.id }))}
        ></MultiSelect>
      </Form.Item>

      <Form.Item label="报警状态" name="status">
        <Select placeholder="请选择" allowClear options={alarmStatusOptions} />
      </Form.Item>
      <Form.Item label="报警标签" name="labelIds">
        <Select
          placeholder="请选择"
          allowClear
          mode="multiple"
          options={(labelList?.list ?? []).map(i => ({ value: i.id, label: i.name }))}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <>
      {filters}
      {!disabled && (
        <div className={styles.tips}>
          <div>
            <Button onClick={unBind} type="link">
              全部移除
            </Button>
            <Button icon={<RedoOutlined rotate={270} />} onClick={updateFn} type="link">
              刷新页面
            </Button>
            <Button type="link">
              <Link to={`/device/rule/list/create`} target="_blank">
                新建规则
              </Link>
              <DoubleRightOutlined />
            </Button>
          </div>
        </div>
      )}

      <Table
        loading={loading}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        sticky
        scroll={{ y: 300 }}
        rowKey="id"
        columns={columns}
        dataSource={ruleGroups}
      />
      <Paging pagingInfo={pagingInfo} />
    </>
  );
};

export default SelectRuleGroupModalChildren;
